import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { FileHeader, FolderName, FileMetadata, MetadataRow, ChipRow, FileIcon } from './styles';
import { getFileIconClass, getFileIconText } from './utils';
import { useTranslation } from '../../i18n';

interface FileCardBodyProps {
  /** The label — a bare name, never a path. */
  name: string;
  isFolder: boolean;
  /** Left metadata cell. Rendered only for a file. */
  primaryMeta?: React.ReactNode;
  /** Right metadata cell — the size, in the browser. */
  secondaryMeta?: React.ReactNode;
  /** A second metadata row: the analysis verdict, or a shared row's chips. */
  extraMeta?: React.ReactNode;
  /**
   * Render the metadata block for a FOLDER as well. Off by default, and the
   * default is the correct one for the file browser: its own folders have
   * nothing to say there — size and modified-date are meaningless for a folder,
   * which is why the block was suppressed in the first place.
   *
   * A **shared** folder is the exception that needs the opt-in. It carries a
   * permission and the path it sits at inside what was shared, and both are
   * exactly as meaningful for a folder as for a file. Without this, a shared
   * grid rendered a file row showing its permission next to a folder row
   * showing an icon and a name and nothing else.
   */
  showMetaForFolder?: boolean;
  /**
   * What sits in the icon box instead of the extension badge. A shared
   * DroneLogbook track has no file, so a file badge would claim one (#1692).
   */
  icon?: React.ReactNode;
  /**
   * The ⋮ button. Left out, no button is drawn: an entry with nothing to act on
   * shows no menu rather than an empty one (#1692).
   */
  onMore?: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * What a file card LOOKS like, with nothing about where it came from.
 *
 * Extracted so "a shared file looks exactly like an own file" (#1247) is true by
 * construction rather than by two components being kept in sync by hand. The
 * browser's `DraggableFileCard` wraps this in dnd-kit drag/drop; the shared
 * grid's `SharedEntryCard` wraps the same thing in nothing. Neither owns the
 * appearance, so neither can drift from the other.
 *
 * The icon is derived from the NAME rather than from a key, which is the same
 * answer — `getFileIconClass` reads the extension — and is the only form a
 * shared entry can supply: it has a registry row and a name, and deliberately no
 * path in this organization's namespace.
 */
const FileCardBody: React.FC<FileCardBodyProps> = ({
  name,
  isFolder,
  primaryMeta,
  secondaryMeta,
  extraMeta,
  showMetaForFolder = false,
  icon,
  onMore,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FileHeader>
        {icon ? (
          <FileIcon className="custom">{icon}</FileIcon>
        ) : (
          <FileIcon className={isFolder ? 'folder' : getFileIconClass(name)}>
            {isFolder ? '📁' : getFileIconText(name)}
          </FileIcon>
        )}
        <FolderName>{name}</FolderName>
      </FileHeader>

      {(!isFolder || showMetaForFolder) && (
        <FileMetadata>
          <MetadataRow>
            <span>{primaryMeta}</span>
            <span>{secondaryMeta}</span>
          </MetadataRow>
          {/* Its own row, and one that WRAPS: "Drone 105 · Read · Granted
              2026-10-22" is wider than a 180px card, and a flex row that does
              not wrap clipped the last chip at the card edge (#1692). */}
          {extraMeta && <ChipRow data-testid="file-card-chips">{extraMeta}</ChipRow>}
        </FileMetadata>
      )}

      {onMore && (
        <Box sx={{ position: 'absolute', top: 2, right: 2, opacity: 0.6 }}>
          <Tooltip title={t('fileManagement.moreActions')}>
            <IconButton
              size="small"
              data-testid="file-card-more"
              onClick={(e) => {
                e.stopPropagation();
                onMore(e);
              }}
              sx={{
                padding: '1px',
                width: '16px',
                height: '16px',
                '&:hover': {
                  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                  opacity: 1,
                },
              }}
            >
              <MoreIcon sx={{ fontSize: '12px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
};

export default FileCardBody;
